import { ComponentPropsWithRef } from 'react';

import { useThemedValue } from '@sternum/web/hooks/useThemedValue';
import * as IconsComponents from '@sternum/web/icons';

import { Colors } from '../theme/constants';

import { Wrapper } from './Icon.styles';

type ColorKey = keyof typeof Colors;

export type IconType = keyof typeof IconsComponents;

export interface Color {
  Dark: ColorKey;
  Light: ColorKey;
}

export interface IconProps extends Omit<ComponentPropsWithRef<'svg'>, 'color'> {
  type: IconType;
  size?: number;
  height?: number;
  width?: number;
  color?: Color;
  color2?: Color;
  hasCursorPointer?: boolean;
  rotate?: number;
}

const defaultColors = { Dark: '', Light: '' };

export function Icon({ type, color, color2, size, height, width, hasCursorPointer, rotate, ...props }: IconProps) {
  // eslint-disable-next-line import/namespace
  const Icon = IconsComponents[type];

  const themedColor1 = (color && { Dark: Colors[color.Dark], Light: Colors[color.Light] }) || defaultColors;
  const themedColor2 = (color2 && { Dark: Colors[color2.Dark], Light: Colors[color2.Light] }) || defaultColors;

  // if colors is not provided then it will now pass the return value of useThemedValue to Icon component
  const themedColorValue1 = useThemedValue(themedColor1) as ColorKey;
  const themedColorValue2 = useThemedValue(themedColor2) as keyof typeof Colors;

  if (!Icon) return null;

  const iconWidth = width || size;
  const iconHeight = height || size;

  const renderedIcon = (
    <Icon
      color={color ? themedColorValue1 : undefined}
      color2={color2 ? themedColorValue2 : undefined}
      width={iconWidth}
      height={iconHeight}
      data-has-cursor-pointer={hasCursorPointer}
      {...props}
    />
  );

  if (rotate === undefined) {
    return renderedIcon;
  }

  return <Wrapper $rotate={rotate}>{renderedIcon}</Wrapper>;
}
